import { isBetaModule } from 'src/app/constants/release-notes';
import { Component, OnInit, OnDestroy, HostListener } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { UserService } from "src/app/service/user.service";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { HttpClient } from "@angular/common/http";
import { HeaderComponent } from "../header/header.component";
import { FooterComponent } from "../footer/footer.component";
import { environment } from "../../../environments/environment";
import { ChatService } from "src/app/service/chat.service";
import { Subscription } from "rxjs";

export interface ModuleCard {
  id: string;
  title: string;
  description: string;
  iconName: string;
  color: string;
  category: string;
  routerLink: string;
}

export interface StockItem {
  name: string;
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  history: number[];
  prevClose: number;
  logoType: string;
  logo?: string;
  isFavourite?: boolean;
  initialIndex?: number;
  favouriteTimestamp?: number;
  linePath?: string;
  areaPath?: string;
}

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.css"],
  imports: [
    HeaderComponent,
    RouterLink,
    FormsModule,
    FooterComponent,
    CommonModule
  ]
})
export class HomeComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private userService: UserService,
    private httpClient: HttpClient,
    private chatService: ChatService
  ) { }

  newsArticles: any[] = [];
  activeStartIndex: number = 0;
  isLoadingNews: boolean = false;
  private autoScrollId: any;
  showTooltipFor: any = null;
  showTitleTooltipFor: any = null;

  stocks: StockItem[] = [];
  isLoadingStocks: boolean = false;
  isUsingLiveStocks: boolean = false;
  private stocksIntervalId: any;



  searchQuery: string = "";
  hoveredCard: string | null = null;
  userModules: string[] = [];
  private modulesSub?: Subscription;
  userName: string = "";
  userInitials: string = "JD";

  greeting: string = "";
  greetingIcon: string = "wb_sunny";
  greetingTheme: string = "morning";
  currentQuote: string = "";
  currentAuthor: string = "";
  private greetingIntervalId: any;

  modules: ModuleCard[] = [
    {
      id: "tasks",
      title: "Tasks",
      description: "Everything waiting on you, in one place. Ask, assign, follow up, done.",
      iconName: "tasks",
      color: "from-indigo-500 to-sky-400",
      category: "Everyday",
      routerLink: "/tasks"
    },
    {
      id: "archive",
      title: "Archive a Project",
      description: "Store and organize completed projects with version control",
      iconName: "upload",
      color: "from-purple-500 to-blue-500",
      category: "Management",
      routerLink: "/upload"
    },
    {
      id: "collections",
      title: "Collections",
      description: "Group and manage related items in organized collections",
      iconName: "database",
      color: "from-blue-500 to-cyan-500",
      category: "Organization",
      routerLink: "/collection"
    },
    {
      id: "inventory",
      title: "Inventory",
      description: "Track stock levels and manage product inventory in real-time",
      iconName: "users",
      color: "from-cyan-500 to-teal-500",
      category: "Management",
      routerLink: "/inventory/dashboard"
    },
    {
      id: "workspace",
      title: "Interactive Tracker",
      description: "Record center for Unity/Unreal/CS team",
      iconName: "workspace",
      color: "from-orange-500 to-red-500",
      category: "Workspace",
      routerLink: "/link-workspace"
    },
    // {
    //   id: "crm",
    //   title: "CRM",
    //   description: "Manage customer relationships and track interactions",
    //   iconName: "user-plus",
    //   color: "from-purple-500 to-pink-500",
    //   category: "Sales",
    //   routerLink: "/crm/leads"
    // },
    {
      id: "leads",
      title: "Lead Tracking",
      description: "Monitor and nurture potential customers through the sales pipeline",
      iconName: "activity",
      color: "from-blue-500 to-indigo-500",
      category: "Sales",
      routerLink: "/lead-track/all-leads"
    },
    {
      id: "profile",
      title: "Create Profile",
      description: "Set up and customize user profiles with detailed information",
      iconName: "user-plus",
      color: "from-indigo-500 to-purple-500",
      category: "Users",
      routerLink: "/create-profile"
    },
    // {
    //   id: "linkai",
    //   title: "Link AI",
    //   description: "Connect AI capabilities to automate and enhance workflows",
    //   iconName: "link",
    //   color: "from-violet-500 to-purple-500",
    //   category: "Integration",
    //   routerLink: "/ai"
    // },
    {
      id: "vault",
      title: "Vault",
      description: "Secure storage for media files and digital assets",
      iconName: "image",
      color: "from-pink-500 to-rose-500",
      category: "Storage",
      routerLink: "/vault"
    },
    {
      id: "knowledge",
      title: "Knowledge base",
      description: "SOPs, policies, templates and the links everyone keeps asking for, in one searchable place",
      iconName: "book",
      color: "from-indigo-500 to-violet-500",
      category: "Storage",
      routerLink: "/vault/knowledge"
    },
    {
      id: "whatsapp-broadcast",
      title: "WhatsApp Broadcast",
      description: "Send bulk WhatsApp messages to customers and leads",
      iconName: "whatsapp",
      color: "from-green-500 to-emerald-500",
      category: "Sales",
      routerLink: "/whatsapp-broadcast"
    },
    {
      id: "reimbursements",
      title: "Reimbursements",
      description: "Submit, track, and approve employee expense and reimbursement claims",
      iconName: "currency_rupee",
      color: "from-amber-500 to-orange-500",
      category: "Finance",
      routerLink: "/reimbursement"
    },
    {
      id: "finance",
      title: "Finance",
      description: "Client invoices, vendor payments, PI/PO workflows and payment tracking",
      iconName: "finance",
      color: "from-blue-600 to-indigo-600",
      category: "Finance",
      routerLink: "/finance"
    }
  ];

  ngOnInit(): void {
    localStorage.setItem('version', 'V3');
    // Reset body scroll styles/classes on initialization to ensure page is scrollable
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.body.style.position = "";
    document.body.style.height = "";
    document.body.style.width = "";

    // 1. Initial orientation from localStorage
    const modulesStr = localStorage.getItem("modules") || "";
    this.userModules = modulesStr
      ? modulesStr.split(",").map(m => m.trim().toUpperCase()).filter(Boolean)
      : [];
    console.log('Initial modules from storage:', this.userModules);

    // 2. Refresh from source to ensure data is correct
    const guestID = localStorage.getItem("guestID");
    if (guestID) {
      this.userService.getUserDetails(guestID).subscribe((res: any) => {
        const modulesData = res?.data?.modules || res?.data?.moduleAccess;
        if (modulesData) {
          const modulesVal = Array.isArray(modulesData) ? modulesData.join(",") : modulesData;
          localStorage.setItem('modules', modulesVal);
          this.userModules = modulesVal
            .split(",")
            .map((m: any) => m.trim().toUpperCase())
            .filter(Boolean);
          console.log('Re-synced modules from backend:', this.userModules);
        }

        const subModulesData = res?.data?.subModules || res?.data?.subModuleAccess;
        if (subModulesData) {
          const subModulesVal = typeof subModulesData === 'object' ? JSON.stringify(subModulesData) : subModulesData;
          localStorage.setItem('subModules', subModulesVal);
        }
      });
    }

    // 3. Live updates when an admin changes this person's access (ACCESS-UPDATED socket event)
    this.modulesSub = this.userService.modules$.subscribe((modules) => {
      this.userModules = modules;
    });

    this.setUserName();
    this.updateGreeting();
    this.fetchQuote();

    // Check greeting every minute to keep it dynamic
    this.greetingIntervalId = setInterval(() => {
      this.updateGreeting();
    }, 60000);

    // Fetch news and initiate auto-scroll
    this.fetchNews();
    this.startAutoScroll();

    // Initialize stocks and start updates
    this.initializeStocks();
    this.startStocksTicking();
  }

  ngOnDestroy(): void {
    if (this.greetingIntervalId) {
      clearInterval(this.greetingIntervalId);
    }
    this.stopAutoScroll();
    this.stopStocksTicking();
    this.modulesSub?.unsubscribe();
  }



  mapStockData(apiStock: any, index: number = 0): StockItem {
    const symbol = apiStock.symbol || "";
    const quote = apiStock.quote || {};
    const price = apiStock.currentPrice !== undefined ? apiStock.currentPrice : (quote.currentPrice || 0);
    const change = apiStock.change !== undefined ? apiStock.change : (quote.change || 0);
    const changePercent = apiStock.changePercent !== undefined ? apiStock.changePercent : (quote.changePercent || 0);
    const prevClose = apiStock.previousClose !== undefined ? apiStock.previousClose : (apiStock.prevClose !== undefined ? apiStock.prevClose : (quote.previousClose || quote.open || price));

    // Find if we already have this stock to preserve and append history
    const existingStock = this.stocks.find(s => s.symbol === symbol);
    let history: number[] = [];
    if (existingStock && existingStock.history && existingStock.history.length > 0) {
      history = [...existingStock.history];
      if (history[history.length - 1] !== price) {
        history = [...history.slice(1), price];
      }
    } else {
      // Generate initial history
      const open = apiStock.open !== undefined ? apiStock.open : (quote.open || prevClose);
      const low = apiStock.low !== undefined ? apiStock.low : (quote.low || Math.min(prevClose, price));
      const high = apiStock.high !== undefined ? apiStock.high : (quote.high || Math.max(prevClose, price));

      history = [
        prevClose,
        open,
        (open + low) / 2,
        low,
        (low + high) / 2,
        high,
        price
      ];
    }

    const logoType = symbol.toLowerCase();
    const isFavourite = apiStock.isFavorite || apiStock.isFavourite || apiStock.isFav || apiStock.favourite || false;

    return {
      name: apiStock.name || symbol,
      symbol: symbol,
      price: price,
      change: change,
      changePercent: changePercent,
      prevClose: prevClose,
      history: history,
      logoType: logoType,
      logo: apiStock.logo || "",
      isFavourite: isFavourite,
      initialIndex: index,
      favouriteTimestamp: isFavourite ? (1000 - index) : undefined,
      linePath: this.generateLinePath(history),
      areaPath: this.generateAreaPath(history)
    };
  }

  sortStocks() {
    this.stocks.sort((a, b) => {
      const aFav = a.isFavourite ? 1 : 0;
      const bFav = b.isFavourite ? 1 : 0;

      if (aFav !== bFav) {
        return bFav - aFav; // Favourited first
      }

      if (a.isFavourite && b.isFavourite) {
        const aTime = a.favouriteTimestamp || 0;
        const bTime = b.favouriteTimestamp || 0;
        return bTime - aTime; // Most recently favourited first
      }

      return (a.initialIndex || 0) - (b.initialIndex || 0); // Keep original index order
    });
  }

  toggleFavourite(stock: StockItem, event: Event) {
    event.stopPropagation();

    const originalState = stock.isFavourite;
    const originalTimestamp = stock.favouriteTimestamp;

    // Optimistically update UI and sort
    stock.isFavourite = !stock.isFavourite;
    if (stock.isFavourite) {
      stock.favouriteTimestamp = Date.now();
    } else {
      stock.favouriteTimestamp = undefined;
    }
    this.sortStocks();

    if (originalState) {
      // Remove from favorites
      this.httpClient.delete<any>(environment.host + `stocks/favourites/${stock.symbol}`).subscribe({
        next: (res) => {
          console.log(`Successfully removed ${stock.symbol} from favourites.`);
        },
        error: (err) => {
          console.error(`Failed to remove ${stock.symbol} from favourites. Reverting UI.`, err);
          stock.isFavourite = originalState;
          stock.favouriteTimestamp = originalTimestamp;
          this.sortStocks();
        }
      });
    } else {
      // Add to favorites
      this.httpClient.post<any>(environment.host + `stocks/favourites/${stock.symbol}`, {}).subscribe({
        next: (res) => {
          console.log(`Successfully added ${stock.symbol} to favourites.`);
        },
        error: (err) => {
          console.error(`Failed to add ${stock.symbol} to favourites. Reverting UI.`, err);
          stock.isFavourite = originalState;
          stock.favouriteTimestamp = originalTimestamp;
          this.sortStocks();
        }
      });
    }
  }

  updateStocksFromQuotes(quotes: any[]) {
    if (!quotes || !Array.isArray(quotes)) return;

    this.stocks = this.stocks.map(stock => {
      const quote = quotes.find(q => q.symbol === stock.symbol);
      if (quote) {
        const newPrice = Number((quote.currentPrice || 0).toFixed(2));
        let newHistory = [...stock.history];
        if (newHistory.length > 0) {
          if (newHistory[newHistory.length - 1] !== newPrice) {
            newHistory = [...newHistory.slice(1), newPrice];
          }
        } else {
          newHistory = [newPrice];
        }

        const change = quote.change !== undefined ? quote.change : (newPrice - stock.prevClose);
        const changePercent = quote.changePercent !== undefined ? quote.changePercent : ((change / stock.prevClose) * 100);

        return {
          ...stock,
          price: newPrice,
          change: Number(change.toFixed(2)),
          changePercent: Number(changePercent.toFixed(2)),
          history: newHistory,
          linePath: this.generateLinePath(newHistory),
          areaPath: this.generateAreaPath(newHistory)
        };
      }
      return stock;
    });
  }

  initializeStocks() {
    this.isLoadingStocks = true;
    this.httpClient.get<any>(environment.host + "stocks").subscribe({
      next: (res) => {
        if (res && res.data && Array.isArray(res.data)) {
          this.isUsingLiveStocks = true;
          this.stocks = res.data.map((item: any, idx: number) => this.mapStockData(item, idx));
          this.sortStocks();
        } else {
          this.isUsingLiveStocks = false;
        }
        this.isLoadingStocks = false;
      },
      error: (err) => {
        console.warn("Failed to fetch stocks from backend, loading fallback mock stocks.", err);
        this.isUsingLiveStocks = false;

        this.isLoadingStocks = false;
      }
    });
  }

  generateLinePath(prices: number[]): string {
    if (!prices || prices.length === 0) return "";
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min || 1;
    const width = 80;
    const height = 24;
    const padding = 2;

    return prices.map((price, idx) => {
      const x = (idx / (prices.length - 1)) * width;
      const y = height - padding - ((price - min) / range) * (height - 2 * padding);
      return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');
  }

  generateAreaPath(prices: number[]): string {
    if (!prices || prices.length === 0) return "";
    const linePath = this.generateLinePath(prices);
    const width = 80;
    const height = 24;
    return `${linePath} L ${width} ${height} L 0 ${height} Z`;
  }

  startStocksTicking() {
    this.stopStocksTicking();
    this.stocksIntervalId = setInterval(() => {
      this.fetchStocksTicking();
    }, 15000);
  }

  fetchStocksTicking() {
    if (this.isUsingLiveStocks) {
      this.httpClient.get<any>(environment.host + "stocks/quotes").subscribe({
        next: (res) => {
          if (res && res.data && Array.isArray(res.data)) {
            this.updateStocksFromQuotes(res.data);
          }
        },
        error: (err) => {
          console.warn("Failed to fetch stock quotes from backend, falling back to local simulation.", err);
          this.updateStockPrices();
        }
      });
    } else {
      this.updateStockPrices();
    }
  }

  stopStocksTicking() {
    if (this.stocksIntervalId) {
      clearInterval(this.stocksIntervalId);
      this.stocksIntervalId = null;
    }
  }

  updateStockPrices() {
    this.stocks = this.stocks.map(stock => {
      const volatility = 0.0012; // slight price swings
      const pct = (Math.random() - 0.48) * volatility;
      const newPrice = Number((stock.price * (1 + pct)).toFixed(2));
      const newHistory = [...stock.history.slice(1), newPrice];
      const newChange = Number((newPrice - stock.prevClose).toFixed(2));
      const newChangePercent = Number(((newChange / stock.prevClose) * 100).toFixed(2));

      return {
        ...stock,
        price: newPrice,
        change: newChange,
        changePercent: newChangePercent,
        history: newHistory,
        linePath: this.generateLinePath(newHistory),
        areaPath: this.generateAreaPath(newHistory)
      };
    });
  }

  refreshStocks() {
    this.isLoadingStocks = true;
    this.httpClient.get<any>(environment.host + "stocks").subscribe({
      next: (res) => {
        if (res && res.data && Array.isArray(res.data)) {
          this.isUsingLiveStocks = true;
          this.stocks = res.data.map((item: any, idx: number) => this.mapStockData(item, idx));
          this.sortStocks();
          this.isLoadingStocks = false;
        } else {
          this.refreshMockStocks();
        }
      },
      error: (err) => {
        console.warn("Failed to refresh stocks from backend, using mock refresh.", err);
        this.refreshMockStocks();
      }
    });
  }

  refreshMockStocks() {
    this.isUsingLiveStocks = false;
    setTimeout(() => {
      this.stocks = this.stocks.map(stock => {
        const changePercent = (Math.random() - 0.5) * 3; // up to +/- 1.5%
        const newPrice = Number((stock.price * (1 + changePercent / 100)).toFixed(2));

        const newHistory = Array.from({ length: 7 }, (_, i) => {
          const progress = i / 6;
          const noise = (Math.random() - 0.5) * 0.015;
          const val = stock.price * (1 + (changePercent / 100) * progress + noise);
          return Number(val.toFixed(2));
        });

        newHistory[newHistory.length - 1] = newPrice;

        const newChange = Number((newPrice - stock.prevClose).toFixed(2));
        const newChangePercent = Number(((newChange / stock.prevClose) * 100).toFixed(2));

        return {
          ...stock,
          price: newPrice,
          change: newChange,
          changePercent: newChangePercent,
          history: newHistory,
          linePath: this.generateLinePath(newHistory),
          areaPath: this.generateAreaPath(newHistory)
        };
      });
      this.isLoadingStocks = false;
    }, 800);
  }

  updateGreeting() {
    const hours = new Date().getHours();
    if (hours >= 5 && hours < 12) {
      this.greeting = "Good Morning";
      this.greetingIcon = "wb_twilight"; // Sunrise
      this.greetingTheme = "morning";
    } else if (hours >= 12 && hours < 17) {
      this.greeting = "Good Afternoon";
      this.greetingIcon = "wb_sunny"; // Sun
      this.greetingTheme = "afternoon";
    } else if (hours >= 17 && hours < 20) {
      this.greeting = "Good Evening";
      this.greetingIcon = "wb_twilight"; // Sunset
      this.greetingTheme = "evening";

    } else {
      this.greeting = "Good Night";
      this.greetingIcon = "nights_stay"; // Moon/Stars
      this.greetingTheme = "night";
    }
  }

  fetchQuote() {
    const token = localStorage.getItem("guestToken");
    const headers = {
      Authorization: `Bearer ${token}`
    };

    this.httpClient.get(environment.host + "quotation/", { headers })
      .subscribe({
        next: (res: any) => {
          if (res?.data?.quotation) {
            this.currentQuote = res.data.quotation;
            this.currentAuthor = res.data.author || "";
          }
        },
        error: (err) => {
          console.warn("Failed to fetch quote from backend API, loading fallback quote.", err);
          this.currentQuote = "Start where you are. Use what you have. Do what you can.";
          this.currentAuthor = "Arthur Ashe";
        }
      });
  }



  get userFirstName(): string {
    const firstName = localStorage.getItem("firstName") || "";
    if (firstName) return firstName;
    const userName = localStorage.getItem("username") || "";
    return userName.split(' ')[0] || "User";
  }

  setUserName() {
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    this.userName = `${firstName} ${lastName}`.trim() || "User";

    const firstInitial = firstName.charAt(0) || "";
    const lastInitial = lastName.charAt(0) || "";
    this.userInitials = `${firstInitial}${lastInitial}`.toUpperCase() || "JD";
  }

  get filteredModules(): ModuleCard[] {
    let result = this.modules.filter(module => this.canAccessModule(module));

    if (!this.searchQuery) {
      return result;
    }
    const query = this.searchQuery.toLowerCase();
    return result.filter(module =>
      module.title.toLowerCase().includes(query) ||
      module.description.toLowerCase().includes(query) ||
      module.category.toLowerCase().includes(query)
    );
  }

  isBeta(id: string): boolean {
    return isBetaModule(id);
  }

  canAccessModule(module: ModuleCard): boolean {
    // Hide WhatsApp module on test backend
    if (module.id === "whatsapp-broadcast" && environment.host.includes("backend-test")) {
      return false;
    }

    if (module.id === "tasks") return true; // Tasks is for everyone
    if (module.id === "knowledge") return true; // The knowledge base is for everyone (reads); curators add to it
    const mapping: Record<string, string> = {
      archive: "ARCHIVE",
      collections: "COLLECTIONS",
      inventory: "INVENTORY",
      crm: "CRM",
      leads: "LEAD-TRACKING",
      profile: "CREATE-PROFILE",
      linkai: "LINK-AI",
      vault: "VAULT",
      "whatsapp-broadcast": "WHATSAPP-BROADCAST",
      workspace: "PROJECT-TRACKER",
      reimbursements: "REIMBURSEMENT",
      finance: "FINANCE",
    };

    const backendModuleName = mapping[module.id]?.toUpperCase();
    if (!backendModuleName) {
      return false;
    }
    return this.userModules.some(m => m.toUpperCase() === backendModuleName);
  }

  onCardHover(moduleId: string) {
    this.hoveredCard = moduleId;
  }

  onCardLeave() {
    this.hoveredCard = null;
  }

  navigateToModule(module: ModuleCard) {
    this.router.navigate([module.routerLink]);
  }

  getMaterialIcon(iconName: string): string {
    const icons: Record<string, string> = {
      upload: "upload",
      database: "storage",
      users: "groups",
      "user-plus": "person_add",
      activity: "show_chart",
      link: "link",
      image: "image",
      chat: "chat",
      whatsapp: "chat",
      workspace: "work_history",
      receipt: "receipt_long",
      currency_rupee: "currency_rupee",
      finance: "payments",
      tasks: "task_alt",
      book: "menu_book"
    };
    return icons[iconName] || "storage";
  }

  fetchNews() {
    this.isLoadingNews = true;
    const token = localStorage.getItem("guestToken");
    const headers = {
      Authorization: `Bearer ${token}`
    };

    this.httpClient.get(environment.host + "news", { headers })
      .subscribe({
        next: (res: any) => {
          const articlesArray = Array.isArray(res) ? res : (res?.data && Array.isArray(res.data) ? res.data : null);
          if (articlesArray) {
            const mappedArticles = articlesArray.map((item: any) => ({
              title: item.title || 'News Update',
              description: item.description || 'Click to read the full story on the publisher\'s website.',
              url: item.link || '',
              urlToImage: item.image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80',
              publishedAt: item.pubDate || new Date().toISOString(),
              source: { name: item.source_name || 'News' }
            }));
            const validArticles = mappedArticles.filter((art: any) => art.title);
            if (validArticles.length > 0) {
              this.newsArticles = validArticles;
              this.isLoadingNews = false;
              this.activeStartIndex = 0;
              return;
            }
          }

        },
        error: (err) => {
          console.warn("Failed to fetch news from backend API, loading fallback articles.", err);
        }
      });
  }



  startAutoScroll() {
    this.stopAutoScroll();
    this.autoScrollId = setInterval(() => {
      this.nextNews();
    }, 5000);
  }

  stopAutoScroll() {
    if (this.autoScrollId) {
      clearInterval(this.autoScrollId);
      this.autoScrollId = null;
    }
  }

  getVisibleCardsCount(): number {
    const width = window.innerWidth;
    if (width <= 768) {
      return 1;
    } else if (width <= 1024) {
      return 2;
    } else {
      return 3;
    }
  }

  get activeIndex(): number {
    if (this.newsArticles.length === 0) return 0;
    const visibleCards = this.getVisibleCardsCount();
    const maxIndex = Math.max(0, this.newsArticles.length - visibleCards);
    if (this.activeStartIndex > maxIndex) {
      this.activeStartIndex = maxIndex;
    }
    return this.activeStartIndex;
  }

  nextNews() {
    if (this.newsArticles.length <= 1) return;
    const visibleCards = this.getVisibleCardsCount();
    const maxIndex = Math.max(0, this.newsArticles.length - visibleCards);
    if (this.activeStartIndex >= maxIndex) {
      this.activeStartIndex = 0;
    } else {
      this.activeStartIndex++;
    }
  }

  prevNews() {
    if (this.newsArticles.length <= 1) return;
    const visibleCards = this.getVisibleCardsCount();
    const maxIndex = Math.max(0, this.newsArticles.length - visibleCards);
    if (this.activeStartIndex <= 0) {
      this.activeStartIndex = maxIndex;
    } else {
      this.activeStartIndex--;
    }
  }

  formatNewsDate(dateStr: string): string {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch (e) {
      return dateStr;
    }
  }

  openArticle(url: string) {
    if (url) {
      window.open(url, "_blank");
    }
  }

  onNewsImageError(article: any) {
    const fallback = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
    if (article.urlToImage !== fallback) {
      article.urlToImage = fallback;
    }
  }

  onSourceHover(event: MouseEvent, article: any) {
    const badge = event.currentTarget as HTMLElement;
    const span = badge.querySelector('span');
    if (span && span.scrollWidth > span.clientWidth) {
      this.showTooltipFor = article;
    } else {
      this.showTooltipFor = null;
    }
  }

  onSourceLeave() {
    this.showTooltipFor = null;
  }

  onTitleHover(event: MouseEvent, article: any) {
    const heading = event.currentTarget as HTMLElement;
    if (heading && heading.scrollHeight > heading.clientHeight) {
      this.showTitleTooltipFor = article;
    } else {
      this.showTitleTooltipFor = null;
    }
  }

  onTitleLeave() {
    this.showTitleTooltipFor = null;
  }

  // Cat cursor tracking state 
  private _catRafId: number | null = null;

  private _catTarget = {
    headX: 0,
    headY: 0,
    headRot: -8,

    leftEyeX: 0,
    leftEyeY: 0,

    rightEyeX: 0,
    rightEyeY: 0,

    earLeft: 0,
    earRight: 0
  };

  private _catCurrent = {
    headX: 0,
    headY: 0,
    headRot: -8,

    leftEyeX: 0,
    leftEyeY: 0,

    rightEyeX: 0,
    rightEyeY: 0,

    earLeft: 0,
    earRight: 0
  };

  private _catVelocity = {
    headX: 0,
    headY: 0,
    headRot: 0,

    leftEyeX: 0,
    leftEyeY: 0,

    rightEyeX: 0,
    rightEyeY: 0,

    earLeft: 0,
    earRight: 0
  };

  private _catLerpActive = false;

  @HostListener('mousemove', ['$event'])
  @HostListener('mousemove', ['$event'])
  onBannerMouseMove(event: MouseEvent) {

    const hostEl = event.currentTarget as HTMLElement;

    const banner =
      hostEl.querySelector('.greeting-banner') as HTMLElement;

    const catEl =
      hostEl.querySelector('.cat-animation-container') as HTMLElement;

    if (!banner || !catEl) return;

    const bannerRect = banner.getBoundingClientRect();
    const catRect = catEl.getBoundingClientRect();

    const headCX =
      catRect.left + catRect.width * 0.42;

    const headCY =
      catRect.top + catRect.height * 0.40;

    const dx = event.clientX - headCX;
    const dy = event.clientY - headCY;

    let nx = dx / (bannerRect.width * 0.55);
    let ny = dy / (bannerRect.height * 0.55);

    const len = Math.sqrt(nx * nx + ny * ny);

    if (len > 1) {
      nx /= len;
      ny /= len;
    }

    const easeX =
      Math.sign(nx) *
      Math.pow(Math.abs(nx), 0.75);

    const easeY =
      Math.sign(ny) *
      Math.pow(Math.abs(ny), 0.75);

    // =====================================
    // Eye convergence
    // =====================================

    const closeFactor =
      Math.max(
        0,
        1 - Math.min(len, 1)
      );

    const convergeAmount =
      closeFactor * 4.5;

    const eyeRangeX = 10.5;
    const eyeRangeY = 13;

    this._catTarget.leftEyeX =
      easeX * eyeRangeX + convergeAmount;

    this._catTarget.rightEyeX =
      easeX * eyeRangeX - convergeAmount;

    this._catTarget.leftEyeY =
      easeY * eyeRangeY;

    this._catTarget.rightEyeY =
      easeY * eyeRangeY;

    // =====================================
    // Head movement
    // =====================================

    this._catTarget.headX =
      easeX * 5;

    this._catTarget.headY =
      easeY * 3;

    this._catTarget.headRot =
      -8 +
      easeX * 10 +
      easeY * 2;

    // =====================================
    // Ear movement
    // =====================================

    this._catTarget.earLeft =
      easeX * -6;

    this._catTarget.earRight =
      easeX * 6;

    this._startCatLerp(catEl);
  }


  @HostListener('mouseleave', ['$event'])
  @HostListener('mouseleave')
  onBannerMouseLeave() {

    const catEl =
      document.querySelector(
        '.cat-animation-container'
      ) as HTMLElement;

    if (!catEl) return;

    this._catTarget.headX = 0;
    this._catTarget.headY = 0;
    this._catTarget.headRot = -8;

    this._catTarget.leftEyeX = 0;
    this._catTarget.leftEyeY = 0;

    this._catTarget.rightEyeX = 0;
    this._catTarget.rightEyeY = 0;

    this._catTarget.earLeft = 0;
    this._catTarget.earRight = 0;

    this._startCatLerp(catEl);
  }

  private _startCatLerp(catEl: HTMLElement) {

    if (this._catLerpActive) return;

    this._catLerpActive = true;

    this._catLerpLoop(catEl);
  }

  private _catLerpLoop(catEl: HTMLElement) {

    const spring = 0.14;
    const damping = 0.82;

    Object.keys(this._catCurrent).forEach((key) => {

      const current =
        (this._catCurrent as any)[key];

      const target =
        (this._catTarget as any)[key];

      (this._catVelocity as any)[key] +=
        (target - current) * spring;

      (this._catVelocity as any)[key] *=
        damping;

      (this._catCurrent as any)[key] +=
        (this._catVelocity as any)[key];
    });

    // head

    catEl.style.setProperty(
      '--head-x',
      `${this._catCurrent.headX}px`
    );

    catEl.style.setProperty(
      '--head-y',
      `${this._catCurrent.headY}px`
    );

    catEl.style.setProperty(
      '--head-rot',
      `${this._catCurrent.headRot}deg`
    );

    // left eye

    catEl.style.setProperty(
      '--eye-x-left',
      `${this._catCurrent.leftEyeX}px`
    );

    catEl.style.setProperty(
      '--eye-y-left',
      `${this._catCurrent.leftEyeY}px`
    );

    // right eye

    catEl.style.setProperty(
      '--eye-x-right',
      `${this._catCurrent.rightEyeX}px`
    );

    catEl.style.setProperty(
      '--eye-y-right',
      `${this._catCurrent.rightEyeY}px`
    );

    // ears

    catEl.style.setProperty(
      '--ear-left-rot',
      `${this._catCurrent.earLeft}deg`
    );

    catEl.style.setProperty(
      '--ear-right-rot',
      `${this._catCurrent.earRight}deg`
    );

    let moving = false;

    Object.keys(this._catCurrent).forEach((key) => {

      const delta =
        Math.abs(
          (this._catTarget as any)[key] -
          (this._catCurrent as any)[key]
        );

      const vel =
        Math.abs(
          (this._catVelocity as any)[key]
        );

      if (delta > 0.02 || vel > 0.02) {
        moving = true;
      }
    });

    if (!moving) {

      this._catLerpActive = false;

      return;
    }

    this._catRafId =
      requestAnimationFrame(
        () => this._catLerpLoop(catEl)
      );
  }
}

